import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/services/api/queryClient';
import { useAuthInit } from '@/hooks/useAuthInit';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/enums';
import { ToastProvider } from '@/components/ui';

import ProtectedRoute from '@/components/routing/ProtectedRoute';
import StaffRoute from '@/components/routing/StaffRoute';
import CustomerRoute from '@/components/routing/CustomerRoute';
import AdminRoute from '@/components/routing/AdminRoute';

import AdminLayout from '@/components/layout/AdminLayout';
import CustomerLayout from '@/components/layout/CustomerLayout';
import SuperAdminLayout from '@/components/layout/SuperAdminLayout';

import LoginPage from '@/pages/LoginPage';
import MagicLinkPage from '@/pages/MagicLinkPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import RegisterCustomerPage from '@/pages/RegisterCustomerPage';
import RegisterBranchPage from '@/pages/RegisterBranchPage';
import DashboardPage from '@/pages/DashboardPage';
import OrdersPage from '@/pages/OrdersPage';
import StaffOrderDetailPage from '@/pages/OrderDetailPage';
import ReceivingPage from '@/pages/ReceivingPage';
import InventoryPage from '@/pages/InventoryPage';
import DeliverySheetPage from '@/pages/DeliverySheetPage';
import ScannerPage from '@/pages/ScannerPage';
import ClientsPage from '@/pages/ClientsPage';
import SettingsPage from '@/pages/SettingsPage';
import BillingPage from '@/pages/BillingPage';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminBranchesPage from '@/pages/admin/AdminBranchesPage';
import AdminApplicationsPage from '@/pages/admin/AdminApplicationsPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import MyOrdersPage from '@/pages/customer/MyOrdersPage';
import OrderDetailPage from '@/pages/customer/OrderDetailPage';
import NotificationsPage from '@/pages/customer/NotificationsPage';
import ProfilePage from '@/pages/customer/ProfilePage';
import NotFoundPage from '@/pages/NotFoundPage';

const AuthInitializer = () => {
  useAuthInit();
  return null;
};

const RootRedirect = () => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === UserRole.ADMIN) return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === UserRole.CUSTOMER) return <Navigate to="/my-orders" replace />;
  return <Navigate to="/dashboard" replace />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthInitializer />
        <ToastProvider />

        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/magic-link/:token" element={<MagicLinkPage />} />
          <Route path="/register" element={<RegisterCustomerPage />} />
          <Route path="/register-branch" element={<RegisterBranchPage />} />

          {/* Change password (protected, any role) */}
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          {/* Staff routes (Manager + Employee) */}
          <Route
            element={
              <ProtectedRoute>
                <StaffRoute>
                  <AdminLayout />
                </StaffRoute>
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:id" element={<StaffOrderDetailPage />} />
            <Route path="/receiving" element={<ReceivingPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/delivery-sheets/:id" element={<DeliverySheetPage />} />
            <Route path="/scanner" element={<ScannerPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/billing" element={<BillingPage />} />
          </Route>

          {/* Admin routes (Super Admin) */}
          <Route
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <SuperAdminLayout />
                </AdminRoute>
              </ProtectedRoute>
            }
          >
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/branches" element={<AdminBranchesPage />} />
            <Route path="/admin/applications" element={<AdminApplicationsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
          </Route>

          {/* Customer routes */}
          <Route
            element={
              <ProtectedRoute>
                <CustomerRoute>
                  <CustomerLayout />
                </CustomerRoute>
              </ProtectedRoute>
            }
          >
            <Route path="/my-orders" element={<MyOrdersPage />} />
            <Route path="/my-orders/:id" element={<OrderDetailPage />} />
            <Route path="/my-notifications" element={<NotificationsPage />} />
            <Route path="/my-profile" element={<ProfilePage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
